import React from 'react';
import './LegalPages.css';

function TermsAndConditionsPage() {
  return (
    <div className="legal-page">
      <div className="legal-hero">
        <h1>Terms and Conditions</h1>
        <p className="last-updated">Last Updated: February 1, 2026</p>
      </div>

      <div className="legal-content">
        <section className="legal-section">
          <h2>1. Acceptance of Terms</h2>
          <p>Welcome to Discover Vanuatu. By accessing or using our website and mobile application (collectively, the "Services"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use our Services.</p>
          <p>These Terms constitute a legally binding agreement between you and Discover Vanuatu. We reserve the right to modify these Terms at any time without prior notice. Your continued use of the Services after any changes indicates your acceptance of the modified Terms.</p>
        </section>

        <section className="legal-section">
          <h2>2. Use of Services</h2>
          <h3>2.1 Eligibility</h3>
          <p>You must be at least 13 years old to use our Services. By using our Services, you represent and warrant that you meet this age requirement.</p>
          
          <h3>2.2 Account Registration</h3>
          <p>Some features may require you to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>
          
          <h3>2.3 Prohibited Activities</h3>
          <p>You agree not to:</p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Post false, misleading, or fraudulent information</li>
            <li>Impersonate any person or entity</li>
            <li>Interfere with or disrupt the Services or servers</li>
            <li>Use automated systems (bots, scrapers) to access the Services</li>
            <li>Transmit viruses, malware, or harmful code</li>
            <li>Harass, abuse, or harm other users or businesses</li>
            <li>Collect or harvest user information without consent</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Business Listings and Content</h2>
          <h3>3.1 User-Generated Content</h3>
          <p>Our platform allows users and businesses to post content, including reviews, ratings, photos, and business information. You are solely responsible for any content you submit.</p>
          
          <h3>3.2 Content License</h3>
          <p>By posting content on our Services, you grant Discover Vanuatu a worldwide, non-exclusive, royalty-free, transferable license to use, reproduce, modify, distribute, and display such content in connection with operating and promoting our Services.</p>
          
          <h3>3.3 Content Accuracy</h3>
          <p>While we strive to provide accurate information, we do not verify all business listings or user-submitted content. You acknowledge that information may be outdated, incomplete, or inaccurate.</p>
        </section>

        <section className="legal-section">
          <h2>4. Advertisements and Promotions</h2>
          <p>Our Services may display advertisements from businesses and third parties. We are not responsible for the content of these advertisements or the products and services advertised. Any interactions with advertisers are solely between you and the advertiser.</p>
          <p>Businesses paying for featured listings or advertisements will receive priority placement, but this does not constitute an endorsement or guarantee of quality by Discover Vanuatu.</p>
        </section>

        <section className="legal-section">
          <h2>5. Intellectual Property Rights</h2>
          <p>All content, features, and functionality of the Services, including but not limited to text, graphics, logos, icons, images, and software, are the exclusive property of Discover Vanuatu or its licensors and are protected by international copyright, trademark, and other intellectual property laws.</p>
          <p>You may not copy, modify, distribute, sell, or lease any part of our Services without our prior written consent.</p>
        </section>

        <section className="legal-section">
          <h2>6. Payment Terms</h2>
          <h3>6.1 Paid Services</h3>
          <p>Certain features and services may require payment, including business listing upgrades, featured advertisements, and premium subscriptions. All fees are stated in the applicable currency and are non-refundable unless otherwise required by law.</p>
          
          <h3>6.2 Billing</h3>
          <p>By providing payment information, you authorize us to charge the applicable fees to your payment method. You are responsible for all charges incurred under your account, including applicable taxes.</p>
          
          <h3>6.3 Price Changes</h3>
          <p>We reserve the right to change our pricing at any time. Price changes will not affect existing subscriptions until renewal.</p>
        </section>

        <section className="legal-section disclaimer">
          <h2>7. No Refund Policy</h2>
          <p><strong>ALL SALES ARE FINAL.</strong> We do not offer refunds, credits, or prorated charges for any paid services, including but not limited to:</p>
          <ul>
            <li>Featured business listings</li>
            <li>Advertisement campaigns (CPV, CPC, or any other pricing model)</li>
            <li>Premium subscriptions or membership fees</li>
            <li>One-time purchases or services</li>
          </ul>
          <p>This no-refund policy applies regardless of:</p>
          <ul>
            <li>Whether you used the full service period</li>
            <li>Technical issues or service interruptions</li>
            <li>Changes to your business or personal circumstances</li>
            <li>Dissatisfaction with results or performance</li>
            <li>Account suspension or termination</li>
          </ul>
          <p>By making a purchase, you acknowledge and accept this no-refund policy.</p>
        </section>

        <section className="legal-section disclaimer">
          <h2>8. Disclaimer of Warranties</h2>
          <p><strong>THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.</strong></p>
          <p>We explicitly disclaim all warranties, including but not limited to:</p>
          <ul>
            <li><strong>Merchantability:</strong> We do not warrant that the Services are suitable for any particular purpose</li>
            <li><strong>Accuracy:</strong> We do not guarantee the accuracy, completeness, or reliability of any content or information</li>
            <li><strong>Quality:</strong> We do not warrant the quality of services provided by businesses listed on our platform</li>
            <li><strong>Availability:</strong> We do not guarantee uninterrupted, timely, secure, or error-free operation</li>
            <li><strong>Results:</strong> We do not guarantee any specific results from using our Services</li>
            <li><strong>Security:</strong> We do not warrant that the Services are free from viruses or harmful components</li>
          </ul>
          <p>You use the Services entirely at your own risk.</p>
        </section>

        <section className="legal-section disclaimer">
          <h2>9. No Obligation Policy</h2>
          <p>Discover Vanuatu has <strong>NO OBLIGATION</strong> to:</p>
          <ul>
            <li>Verify the identity, credentials, or legitimacy of businesses or users</li>
            <li>Monitor, screen, edit, or remove any content, listings, reviews, or advertisements</li>
            <li>Resolve disputes between users and businesses</li>
            <li>Provide customer support or respond to inquiries</li>
            <li>Investigate complaints or take action against violating users</li>
            <li>Maintain any specific features, functions, or services</li>
            <li>Ensure compatibility with any devices or platforms</li>
            <li>Provide advance notice of changes, updates, or discontinuation</li>
            <li>Compensate for any losses, damages, or inconveniences</li>
            <li>Honor promotional offers, discounts, or special arrangements beyond their stated terms</li>
          </ul>
          <p>We reserve the right to modify, suspend, or terminate any aspect of the Services at any time, with or without notice, for any reason or no reason.</p>
        </section>

        <section className="legal-section disclaimer">
          <h2>10. Limitation of Liability</h2>
          <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, DISCOVER VANUATU AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND LICENSORS SHALL NOT BE LIABLE FOR:</strong></p>
          <ul>
            <li>Any indirect, incidental, special, consequential, or punitive damages</li>
            <li>Loss of profits, revenue, data, or business opportunities</li>
            <li>Cost of substitute services or products</li>
            <li>Personal injury or property damage</li>
            <li>Unauthorized access to or use of our servers or your information</li>
            <li>Errors, mistakes, or inaccuracies in content</li>
            <li>Any dealings with businesses or users through our platform</li>
            <li>Any content or conduct of third parties</li>
          </ul>
          <p>Our total liability for any claims arising from your use of the Services shall not exceed the amount you paid us in the twelve (12) months preceding the claim, or $100 USD, whichever is less.</p>
        </section>

        <section className="legal-section">
          <h2>11. Indemnification</h2>
          <p>You agree to indemnify, defend, and hold harmless Discover Vanuatu and its affiliates from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:</p>
          <ul>
            <li>Your use of the Services</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
            <li>Any content you submit or transmit through the Services</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>12. Third-Party Services and Links</h2>
          <p>Our Services may contain links to third-party websites, applications, or services. We do not endorse, control, or assume responsibility for any third-party content or services. You access third-party services at your own risk and subject to their terms and conditions.</p>
        </section>

        <section className="legal-section">
          <h2>13. Termination</h2>
          <p>We may suspend or terminate your access to the Services at any time, with or without cause, with or without notice. Reasons for termination may include, but are not limited to:</p>
          <ul>
            <li>Violation of these Terms</li>
            <li>Fraudulent or illegal activity</li>
            <li>Requests by law enforcement</li>
            <li>Unexpected technical issues</li>
            <li>Extended periods of inactivity</li>
          </ul>
          <p>Upon termination, your right to use the Services will immediately cease. Termination does not entitle you to any refunds or compensation.</p>
        </section>

        <section className="legal-section">
          <h2>14. Governing Law and Dispute Resolution</h2>
          <p>These Terms shall be governed by and construed in accordance with the laws of Vanuatu, without regard to its conflict of law provisions.</p>
          <p>Any disputes arising from these Terms or your use of the Services shall be resolved through binding arbitration in Vanuatu, except where prohibited by law. You waive any right to participate in class action lawsuits or class-wide arbitration.</p>
        </section>

        <section className="legal-section">
          <h2>15. Severability</h2>
          <p>If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.</p>
        </section>

        <section className="legal-section">
          <h2>16. Entire Agreement</h2>
          <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and Discover Vanuatu regarding the use of our Services and supersede all prior agreements and understandings.</p>
        </section>

        <section className="legal-section">
          <h2>17. Contact Information</h2>
          <p>For questions or concerns regarding these Terms and Conditions, please contact us at:</p>
          <p className="contact-info">
            <strong>Email:</strong> legal@discovervanuatu.com<br/>
            <strong>Address:</strong> Port Vila, Vanuatu
          </p>
        </section>
      </div>
    </div>
  );
}

export default TermsAndConditionsPage;
